import { useInvoiceStore } from '@/stores/invoice';
import { usePrinterStore } from '@/stores/printer';
import { triggerSensitiveDataSync } from '@/utils/http';
import { logInfo } from '@/utils/logger';
import { InvoiceStatus, PrintJobStatus } from '@posx/core';

const TERMINAL_STATUS = [PrintJobStatus.Printed, PrintJobStatus.Failed, PrintJobStatus.Expired];
const BATCH_SIZE = 500;
const MAX_ROUNDS = 300;

async function bulkDeleteByStatus(table: { where: Function; bulkDelete: Function }, statuses: unknown[]) {
  let deleted = 0;
  for (let i = 0; i < MAX_ROUNDS; i++) {
    const roundAt = Date.now();
    const keys = await table.where('status').anyOf(statuses).limit(BATCH_SIZE).primaryKeys();
    if (!keys.length) break;
    await table.bulkDelete(keys);
    deleted += keys.length;
    if (keys.length < BATCH_SIZE) break;
    await new Promise(r => setTimeout(r, Math.min(Date.now() - roundAt, 1000))); // throttle so slow devices back off automatically
  }
  return deleted;
}

// Queued jobs are kept as they turn terminal on timeout
export async function clearFinishedPrintJobs() {
  const startedAt = Date.now();
  const deleted = await bulkDeleteByStatus((await usePrinterStore().getPrintJobService()).table, TERMINAL_STATUS);
  logInfo('Cleanup finished print jobs', { deleted, ms: Date.now() - startedAt });
}

export async function clearCompletedInvoices() {
  const startedAt = Date.now();
  const deleted = await bulkDeleteByStatus((await useInvoiceStore().getService()).table, [InvoiceStatus.Paid, InvoiceStatus.Void]);
  logInfo('Cleanup completed invoices', { deleted, ms: Date.now() - startedAt });
}

let isRunning = false;
export async function cleanupOnShiftClose() {
  if (isRunning) return;
  isRunning = true;
  try {
    await clearFinishedPrintJobs();
    await clearCompletedInvoices();
  } finally { isRunning = false; }
}

export async function cleanupSessionData(): Promise<void> {
  await (await useInvoiceStore().getService()).table.clear();
  triggerSensitiveDataSync();
}
